import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { EVENTO_SEM_PLANO, getPlano, PASSAM_PELO_PLANO, SituacaoDoPlano } from '../services/api';
import TelaDeCarga from './TelaDeCarga';
import TelaDePlanos from './TelaDePlanos';

/**
 * O portão do plano (20/10): entre o login e o app.
 *
 * Pergunta ao servidor se esta pessoa usa o app. Com a cobrança desligada, ou
 * em qualquer estado que passa, o app abre como sempre. Sem plano, a tela de
 * planos fica no lugar dele.
 *
 * Erro ao perguntar NÃO fecha o app: quem pagou não pode ficar trancado por
 * uma falha nossa, e cada rota tem a própria trava no servidor de qualquer
 * forma. Se a rota responder 402, o evento traz a tela de planos.
 */
/** A situação do plano, para o menu saber se mostra "Meu plano" (22/10). */
const SituacaoContexto = createContext<SituacaoDoPlano | null>(null);
export const useSituacaoDoPlano = (): SituacaoDoPlano | null => useContext(SituacaoContexto);

const PortaoDoPlano: React.FC<{ children: React.ReactNode; onSair: () => void }> = ({ children, onSair }) => {
  const [situacao, setSituacao] = useState<SituacaoDoPlano | null>(null);
  const [carregando, setCarregando] = useState(true);

  const perguntar = useCallback(async () => {
    try {
      setSituacao(await getPlano());
    } catch {
      setSituacao(null);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { perguntar(); }, [perguntar]);

  useEffect(() => {
    const aoPerder = () => { perguntar(); };
    window.addEventListener(EVENTO_SEM_PLANO, aoPerder);
    return () => window.removeEventListener(EVENTO_SEM_PLANO, aoPerder);
  }, [perguntar]);

  // Voltou da aba do pagamento: confere na hora, sem esperar os 15 s.
  useEffect(() => {
    const aoVoltar = () => { if (document.visibilityState === 'visible') void perguntar(); };
    document.addEventListener('visibilitychange', aoVoltar);
    return () => document.removeEventListener('visibilitychange', aoVoltar);
  }, [perguntar]);

  // Esperando o pagamento: confere de novo a cada 15 s, para o app abrir sozinho.
  const esperandoPagamento = situacao?.estado === 'pedido';
  useEffect(() => {
    if (!esperandoPagamento) return;
    const t = window.setInterval(perguntar, 15_000);
    return () => window.clearInterval(t);
  }, [esperandoPagamento, perguntar]);

  if (carregando) return <TelaDeCarga mensagem="Entrando na sua conta..." />;
  if (!situacao || !situacao.cobrando || PASSAM_PELO_PLANO.includes(situacao.estado)) {
    return <SituacaoContexto.Provider value={situacao}>{children}</SituacaoContexto.Provider>;
  }
  return <TelaDePlanos situacao={situacao} onAtualizar={setSituacao} onSair={onSair} />;
};

export default PortaoDoPlano;
